'use client';

import { useEffect, useState } from 'react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

export function TableOfContents() {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeHeading, setActiveHeading] = useState<string>('');
  const [isHovered, setIsHovered] = useState(false);
  const [scrollDepth, setScrollDepth] = useState(0);

  useEffect(() => {
    const articleElement = document.querySelector('article');
    if (!articleElement) return;

    const headingElements = articleElement.querySelectorAll(
      'h1, h2, h3, h4, h5, h6'
    );
    const headingsData: Heading[] = [];

    headingElements.forEach((heading, index) => {
      if (!heading.id) {
        heading.id = `heading-${index}`;
      }
      headingsData.push({
        id: heading.id,
        text: heading.textContent || '',
        level: parseInt(heading.tagName.charAt(1), 10)
      });
    });

    setHeadings(headingsData);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0% -35% 0%' }
    );

    headingElements.forEach((heading) => {
      observer.observe(heading);
    });

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollDepth(Math.min(scrollPercent, 100));
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [headings.length]); // Wait for headings to populate

  const scrollToHeading = (headingId: string) => {
    const element = document.getElementById(headingId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (headings.length === 0) return null;

  return (
    <>

      {/* Premium Minimalist Table of Contents (Desktop Only) */}
      <aside
        className='group fixed top-1/2 left-1.5 z-50 hidden -translate-y-1/2 items-start lg:flex xl:left-3'
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        {/* Left Pill - Visual Track */}
        <div className='bg-muted/50 dark:bg-muted/20 border-border/40 flex w-10 flex-col items-center rounded-full border py-3 shadow-lg backdrop-blur-md transition-all'>
          {/* Spacer corresponding to the header line in the right panel */}
          <div className='mb-2 h-6' />

          {headings.map((item) => {
            const isActive = activeHeading === item.id;
            const level = item.level;
            return (
              <button
                key={`track-${item.id}`}
                type='button'
                onClick={() => scrollToHeading(item.id)}
                className='flex h-5 w-full cursor-pointer items-center justify-center px-1 focus:outline-none'
                aria-label={`Scroll to ${item.text}`}>
                <div
                  className={`h-[2px] rounded-full transition-all duration-300 ${
                    isActive
                      ? 'w-full bg-emerald-600 shadow-[0_0_8px_rgba(5,150,105,0.4)]'
                      : level === 2
                        ? 'w-3 bg-slate-400/60 dark:bg-slate-500/60'
                        : 'w-1.5 bg-slate-400/40 dark:bg-slate-500/40'
                  }`}
                />
              </button>
            );
          })}

          {/* Bottom padding */}
          <div className='h-4' />
        </div>

        {/* Right Card - Text Content (Expands on Hover) */}
        <div
          className={`absolute top-1/2 left-[3.5rem] flex max-h-[85vh] origin-left -translate-y-1/2 transition-all duration-300 ease-out ${
            isHovered
              ? 'pointer-events-auto translate-x-0 scale-100 opacity-100'
              : 'pointer-events-none -translate-x-4 scale-95 opacity-0'
          }`}>
          <div className='bg-card/98 border-border/50 custom-scrollbar flex w-85 flex-col overflow-y-auto rounded-3xl border py-3 shadow-2xl backdrop-blur-2xl'>
            <div className='mb-3 flex items-center px-6 text-[10px] font-bold tracking-[0.25em] text-emerald-600/80 uppercase'>
              Contents
            </div>

            <div className='flex flex-1 flex-col pb-2'>
              {headings.map((item) => {
                const isActive = activeHeading === item.id;
                const level = item.level;

                return (
                  <button
                    key={`text-${item.id}`}
                    type='button'
                    onClick={() => scrollToHeading(item.id)}
                    className={`group/item relative flex min-h-[1.75rem] w-full items-center px-8 py-1 text-left transition-all focus:outline-none ${
                      isActive
                        ? 'bg-emerald-500/5 text-[13px] font-semibold text-emerald-700 dark:text-emerald-400'
                        : 'hover:text-foreground hover:bg-muted/40 text-[10px] text-slate-500/80'
                    }`}
                    style={{
                      paddingLeft: `${level > 2 ? (level - 2) * 0.75 + 2 : level === 2 ? 2 : 2}rem`
                    }}>
                    <span
                      className={`truncate transition-transform group-hover/item:translate-x-1 ${isActive ? 'translate-x-1' : ''}`}>
                      {item.text}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
